import { DateTime } from "luxon";
import { formatMinutes } from "./format";

/**
 * Series colours in fixed slot order, validated with the dataviz palette checker (lightness,
 * chroma, colour-vision separation). Slots 3 and 4 sit below 3:1 on white, which is why every
 * chart also has a legend and a table view. Never assign by rank or cycle past the end.
 */
export const SERIES_COLORS: readonly string[] = ["#ED1845", "#2a78d6", "#eda100", "#1baf7a", "#4a3aa7"];

/** Smallest of 1, 2 or 5 times a power of ten that is at least `max`; 1 for an empty chart. */
export function niceMax(max: number): number {
  if (!(max > 0)) return 1;
  const exp = 10 ** Math.floor(Math.log10(max));
  const f = max / exp;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return Number((nice * exp).toPrecision(12));
}

/** Ticks from 0 to `niceMax(max)` in whole-number steps where possible (5, 4 or 2 intervals), else just the ends. */
export function ticks(max: number): number[] {
  const top = niceMax(max);
  const step = [top / 5, top / 4, top / 2].find((s) => Number.isInteger(s));
  if (step === undefined) return [0, top];
  const out: number[] = [];
  for (let v = 0; v <= top; v += step) out.push(v);
  return out;
}

export function scale(domainMax: number, rangePx: number): (v: number) => number {
  const top = domainMax > 0 ? domainMax : 1;
  return (v) => (v / top) * rangePx;
}

/** Label every point for a week of days, every 7th for longer day ranges, and about seven labels for dense samples. */
export function labelEvery(count: number): number {
  if (count <= 7) return 1;
  if (count <= 90) return 7;
  return Math.max(1, Math.floor(count / 7));
}

export const shortDay = (iso: string): string => DateTime.fromISO(iso, { zone: "utc" }).toFormat("LLL d");
export const shortTime = (iso: string): string => DateTime.fromISO(iso).toFormat("LLL d, h:mm a");

export function formatSeconds(ms: number): string {
  const s = ms / 1000;
  return s >= 10 ? `${Math.round(s)}s` : `${Number(s.toFixed(1))}s`;
}

/** Short y-axis label for a wait given in minutes: `5 min`, `2 h`, `1 h 30 min`. */
export const formatWaitLabel = (min: number): string => formatMinutes(Math.round(min));
